import type { ReadFileResult } from './types.js';

export type LineZeroStreamState = {
  line0Accum: string;
  skipLine0Chars: number;
  line0CharsReturned: number;
};

export function createLineZeroStreamState(lineOffsetIn: number | undefined): LineZeroStreamState {
  return {
    line0Accum: '',
    skipLine0Chars: typeof lineOffsetIn === 'number' ? lineOffsetIn : 0,
    line0CharsReturned: 0,
  };
}

export function shouldTrackLineZero(startLine: number, lineOffsetIn: number | undefined): boolean {
  return startLine === 0 || lineOffsetIn !== undefined;
}

function stripCarriageReturn(lineContent: string): string {
  return lineContent.endsWith('\r') ? lineContent.slice(0, -1) : lineContent;
}

export function lineContentBeforeNewline(text: string, lineStart: number, newlineIndex: number): string {
  let lineEnd = newlineIndex;
  if (lineEnd > lineStart && text[lineEnd - 1] === '\r') lineEnd--;
  return text.slice(lineStart, lineEnd);
}

export function buildLineZeroChunkResult(
  resultLines: string[],
  fileSize: number,
  lineOffset: number,
  totalLines = 1,
): ReadFileResult {
  return {
    content: resultLines.join('\n'),
    size: fileSize,
    lineOffset,
    totalLines,
  };
}

export function tryFinishLineZeroOnCompleteLine(
  state: LineZeroStreamState,
  lineContent: string,
  resultLines: string[],
  fileSize: number,
  lineChunkSize: number,
  lineOffsetIn: number | undefined,
  endLine: number,
  currentLine: number,
): ReadFileResult | 'continue' | 'done' {
  state.line0Accum += lineContent;
  const totalLine0 = state.line0Accum.length;

  if (state.skipLine0Chars > 0) {
    if (totalLine0 <= state.skipLine0Chars) {
      state.skipLine0Chars -= totalLine0;
      state.line0Accum = '';
      return currentLine + 1 > endLine ? 'done' : 'continue';
    }
    const from = state.skipLine0Chars;
    const take = Math.min(lineChunkSize, totalLine0 - from);
    resultLines.push(state.line0Accum.slice(from, from + take));
    state.line0CharsReturned += take;
    if (from + take < totalLine0) {
      return buildLineZeroChunkResult(
        resultLines,
        fileSize,
        (lineOffsetIn ?? 0) + state.line0CharsReturned,
      );
    }
    state.line0Accum = '';
    state.skipLine0Chars = 0;
    state.line0CharsReturned = 0;
    return 'continue';
  }

  if (totalLine0 > lineChunkSize) {
    resultLines.push(state.line0Accum.slice(0, lineChunkSize));
    return buildLineZeroChunkResult(resultLines, fileSize, lineChunkSize);
  }

  resultLines.push(state.line0Accum);
  state.line0Accum = '';
  return 'continue';
}

export function tryFinishLineZeroPartial(
  state: LineZeroStreamState,
  partialLine: string,
  resultLines: string[],
  fileSize: number,
  lineChunkSize: number,
  lineOffsetIn: number | undefined,
): ReadFileResult | null {
  state.line0Accum += partialLine;
  const totalLine0 = state.line0Accum.length;

  if (state.skipLine0Chars > 0) {
    if (totalLine0 <= state.skipLine0Chars) {
      state.skipLine0Chars -= totalLine0;
      state.line0Accum = '';
      return null;
    }
    const from = state.skipLine0Chars;
    const take = Math.min(lineChunkSize, totalLine0 - from);
    resultLines.push(state.line0Accum.slice(from, from + take));
    return buildLineZeroChunkResult(resultLines, fileSize, (lineOffsetIn ?? 0) + take);
  }

  if (totalLine0 > lineChunkSize) {
    resultLines.push(state.line0Accum.slice(0, lineChunkSize));
    return buildLineZeroChunkResult(resultLines, fileSize, lineChunkSize);
  }

  return null;
}

export function finishLineZeroTail(
  state: LineZeroStreamState,
  tail: string,
  resultLines: string[],
  fileSize: number,
  lineChunkSize: number,
  lineOffsetIn: number | undefined,
): ReadFileResult | null {
  if (tail.length === 0) return null;
  state.line0Accum += stripCarriageReturn(tail);
  const totalLine0 = state.line0Accum.length;

  if (state.skipLine0Chars > 0) {
    if (totalLine0 <= state.skipLine0Chars) {
      return { content: resultLines.join('\n'), size: fileSize };
    }
    const from = state.skipLine0Chars;
    const take = Math.min(lineChunkSize, totalLine0 - from);
    resultLines.push(state.line0Accum.slice(from, from + take));
    state.line0CharsReturned += take;
    if (from + take < totalLine0) {
      return buildLineZeroChunkResult(
        resultLines,
        fileSize,
        (lineOffsetIn ?? 0) + state.line0CharsReturned,
      );
    }
    return null;
  }

  if (totalLine0 > lineChunkSize) {
    resultLines.push(state.line0Accum.slice(0, lineChunkSize));
    return buildLineZeroChunkResult(resultLines, fileSize, lineChunkSize);
  }

  resultLines.push(state.line0Accum);
  return null;
}
